import { toast } from 'sonner';
import { Loader2, Plus } from 'lucide-react';
import { type FormEvent, useState } from 'react';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { SchemaFieldBuilder } from './SchemaFieldBuilder';
import { useCreateTemplate } from '../api/templates-api';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

interface TemplateFormDialogProps {
  category?: string;
}

export function TemplateFormDialog({ category = 'document' }: TemplateFormDialogProps) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [systemPrompt, setSystemPrompt] = useState('');
  const [instructions, setInstructions] = useState('');
  const [jsonSchema, setJsonSchema] = useState<Record<string, unknown>>({});

  const createTemplate = useCreateTemplate();

  function resetForm() {
    setName('');
    setDescription('');
    setSystemPrompt('');
    setInstructions('');
    setJsonSchema({});
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();

    if (!name.trim()) return;
    if (!instructions.trim()) return;

    try {
      await createTemplate.mutateAsync({
        name: name.trim(),
        json_schema: jsonSchema,
        instructions: instructions.trim(),
        description: description.trim() || undefined,
        system_prompt: systemPrompt.trim() || undefined,
        category,
      });
      toast.success('Template created successfully');
      resetForm();
      setOpen(false);
    } catch {
      toast.error('Failed to create template');
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
      <DialogTrigger asChild>
        <Button size="sm">
          <Plus className="mr-2 h-4 w-4" />
          Add Template
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Template</DialogTitle>
        </DialogHeader>
        <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
          <div className="flex flex-col gap-2">
            <Label htmlFor="template-name">Name</Label>
            <Input
              id="template-name"
              required
              value={name}
              placeholder="Template name"
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="template-description">Description</Label>
            <Textarea
              id="template-description"
              rows={2}
              value={description}
              placeholder="Template description"
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="template-system-prompt">System Prompt</Label>
            <Textarea
              id="template-system-prompt"
              rows={3}
              value={systemPrompt}
              placeholder="System prompt for the AI"
              onChange={(e) => setSystemPrompt(e.target.value)}
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="template-instructions">Instructions</Label>
            <Textarea
              id="template-instructions"
              required
              rows={3}
              value={instructions}
              placeholder="Instructions for generating the report"
              onChange={(e) => setInstructions(e.target.value)}
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label>Context Fields</Label>
            <SchemaFieldBuilder value={jsonSchema} onChange={setJsonSchema} />
          </div>

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={createTemplate.isPending}>
              {createTemplate.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
